import { motion } from 'framer-motion';
import { camelCase } from 'lodash';
import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from 'reflexbox';
import styled from 'styled-components';

import {
	MeetingFragment,
	MeetingSeriesRecurrenceFrequency,
	MeetingTargetInput,
	MeetingTargetType,
} from '@treams/treams-network';
import { FlexGap, Label, Paragraph, Select } from '@treams/treams-ui';

const DISABLED_OPTION_VALUE = '__NEVER';

const orderedRecurrenceFrequencies = [
	MeetingSeriesRecurrenceFrequency.Weekly,
	MeetingSeriesRecurrenceFrequency.Biweekly,
	MeetingSeriesRecurrenceFrequency.Triweekly,
	MeetingSeriesRecurrenceFrequency.Quadweekly,
	MeetingSeriesRecurrenceFrequency.Hexweekly,
	MeetingSeriesRecurrenceFrequency.Monthly,
	MeetingSeriesRecurrenceFrequency.Bimonthly,
	MeetingSeriesRecurrenceFrequency.Quarterly,
];

// If (new) recurrence frequencies are not yet in the ordered list, they will be placed at the end.
const recurrenceFrequencyOrder = (frequency: MeetingSeriesRecurrenceFrequency) =>
	orderedRecurrenceFrequencies.includes(frequency)
		? orderedRecurrenceFrequencies.indexOf(frequency)
		: Infinity;

const SeriesInputWrapper = styled(FlexGap).attrs({
	gap: 8,
	alignItems: 'center',
})`
	> * {
		width: 100%;
		flex-grow: 1;
	}
`;

export function SeriesInput(props: {
	series: MeetingFragment['series'];
	recurrenceFrequency: MeetingSeriesRecurrenceFrequency | undefined;
	onChangeRecurrenceFrequency: (value: MeetingSeriesRecurrenceFrequency | undefined) => void;
	target: MeetingTargetInput;
	renderSeriesMetadataFields?: (frequency: MeetingSeriesRecurrenceFrequency) => ReactNode;
	disabled?: boolean;
}) {
	const [lastSelectedFrequency, setLastSelectedFrequency] = useState(
		props.recurrenceFrequency ?? MeetingSeriesRecurrenceFrequency.Weekly,
	);

	useEffect(() => {
		if (props.recurrenceFrequency) {
			setLastSelectedFrequency(props.recurrenceFrequency);
		}
	}, [props.recurrenceFrequency]);

	return (
		<>
			<RecurrenceFrequencyInput
				value={props.recurrenceFrequency}
				onChange={props.onChangeRecurrenceFrequency}
				targetType={props.target.type}
				disabled={props.disabled}
			/>
			<motion.div
				initial={{ height: 0, opacity: 0 }}
				animate={
					props.recurrenceFrequency ? { height: 'auto', opacity: 1 } : { height: 0, opacity: 0 }
				}
				transition={{ type: 'spring', stiffness: 300, damping: 30 }}
				style={{ overflow: props.recurrenceFrequency ? undefined : 'hidden' }}
			>
				<FlexGap gap={16} flexDirection="column" mt={16}>
					{props.renderSeriesMetadataFields?.(lastSelectedFrequency)}
				</FlexGap>
			</motion.div>
		</>
	);
}

function RecurrenceFrequencyInput(props: {
	value: MeetingSeriesRecurrenceFrequency | undefined;
	onChange: (value: MeetingSeriesRecurrenceFrequency | undefined) => void;
	targetType: MeetingTargetType;
	disabled?: boolean;
}) {
	const { t } = useTranslation();

	const enabledOptions = useMemo(
		() =>
			Object.values(MeetingSeriesRecurrenceFrequency)
				.sort((a, b) => recurrenceFrequencyOrder(a) - recurrenceFrequencyOrder(b))
				.map((frequency) => ({
					value: frequency,
					title: t(`meeting.series.recurrence.frequency.${camelCase(frequency)}`),
				})),

		[t],
	);

	const options = [
		{
			value: DISABLED_OPTION_VALUE,
			title: t('meeting.series.recurrence.never'),
		},
	].concat(enabledOptions);

	return (
		<FlexGap gap={8} flexDirection="column">
			<Label>{t('meeting.series.repeatLabel')}</Label>
			<SeriesInputWrapper>
				<Select
					disabled={props.disabled}
					options={options}
					selectedOption={options.find(
						(option) => option.value === (props.value ?? DISABLED_OPTION_VALUE),
					)}
					onChange={(value) =>
						value === DISABLED_OPTION_VALUE
							? props.onChange(undefined)
							: props.onChange(value as MeetingSeriesRecurrenceFrequency)
					}
					search={false}
				/>
				<Box>
					<Paragraph small ml={2}>
						{t('meeting.series.repeatsTarget', {
							targets: t(`meeting.targetPlural.${camelCase(props.targetType)}`),
						})}
					</Paragraph>
				</Box>
			</SeriesInputWrapper>
		</FlexGap>
	);
}
